import { useState } from 'react';
import { X, CheckCircle, AlertTriangle, MessageSquareWarning, ShieldAlert, HelpCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useModalDismiss } from '../../hooks/useModalDismiss';

const REPORT_REASONS = [
  {
    id: 'spam',
    icon: MessageSquareWarning,
    title: 'Spam',
    description: 'Unwanted promotion, repeated posting, or off-topic content',
  },
  {
    id: 'harassment',
    icon: ShieldAlert,
    title: 'Harassment or bullying',
    description: 'Targets or demeans a person or group',
  },
  {
    id: 'inappropriate',
    icon: AlertTriangle,
    title: 'Inappropriate content',
    description: 'Explicit, hateful, or shares private client details',
  },
  {
    id: 'other',
    icon: HelpCircle,
    title: 'Something else',
    description: "Doesn't fit the above — tell us more below",
  },
];

// Small report-reason picker.
// contentType: 'post' | 'reply' | 'video' | 'profile' | 'video_comment'
const TYPE_LABELS = { profile: 'user', video_comment: 'comment' };
const ReportModal = ({ contentType, contentId, onClose }) => {
  useModalDismiss(onClose);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    const { error } = await supabase.rpc('submit_report', {
      p_content_type: contentType,
      p_content_id: String(contentId),
      p_reason: selected,
      p_details: details.trim() || null,
    });

    setSubmitting(false);
    if (error) {
      setSubmitError(
        error.message === 'cannot_report_self'
          ? "You can't report yourself."
          : 'Something went wrong. Please try again.'
      );
      return;
    }
    setSuccess(true);
    setTimeout(onClose, 2000);
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div
          className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl p-8 text-center animate-fadeIn"
          style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
          onClick={e => e.stopPropagation()}
        >
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            <CheckCircle size={32} className="text-white" />
          </div>
          <h3 className="text-xl font-bold text-primary mb-2">Report submitted</h3>
          <p className="text-sm text-tertiary">Thanks — our moderators will review this.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl overflow-hidden animate-fadeIn"
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-lg font-bold text-primary">Report this {TYPE_LABELS[contentType] || contentType}</h3>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1.5 rounded-lg transition-all hover:scale-110 active:scale-95"
              style={{ color: 'var(--text-tertiary)' }}
            >
              <X size={18} />
            </button>
          </div>
          <p className="text-sm text-tertiary mb-4">What's the problem? Reports are anonymous to other members.</p>

          <div className="space-y-2 mb-4">
            {REPORT_REASONS.map(({ id, icon: Icon, title, description }) => (
              <button
                key={id}
                onClick={() => setSelected(id)}
                className="w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all duration-200 active:scale-[0.98]"
                style={{
                  backgroundColor: selected === id ? 'var(--pill-bg)' : 'var(--input-bg)',
                  border: `1px solid ${selected === id ? 'var(--brand-primary)' : 'var(--card-border)'}`,
                }}
              >
                <Icon
                  size={18}
                  className="flex-shrink-0 mt-0.5"
                  style={{ color: selected === id ? 'var(--brand-primary)' : 'var(--text-tertiary)' }}
                />
                <span>
                  <span className="block text-sm font-semibold text-primary">{title}</span>
                  <span className="block text-xs text-tertiary">{description}</span>
                </span>
              </button>
            ))}
          </div>

          <textarea
            value={details}
            onChange={e => setDetails(e.target.value)}
            maxLength={1000}
            rows={2}
            placeholder={selected === 'other' ? 'Please describe the issue' : 'Add details (optional)'}
            className="w-full rounded-xl px-3 py-2 text-sm text-primary mb-3 resize-none focus:outline-none"
            style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--card-border)' }}
          />

          {submitError && (
            <p className="text-xs mb-3" style={{ color: '#dc2626' }}>{submitError}</p>
          )}

          <button
            onClick={handleSubmit}
            disabled={!selected || submitting}
            className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            {submitting ? 'Submitting…' : 'Submit report'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportModal;
